/**
 * Online Lobby: QUICK MATCH · RANKED · PRIVATE ROOM · LEADERBOARD tabs, a player card (name, rating, tier,
 * record) with the head picker, connection status with retry, queue search state, private room create/join with
 * the 4-letter code, ready toggles and the room roster, and the ranked leaderboard.
 *
 * State comes from `ui.net` (mirrored by the online client) plus raw server messages for the leaderboard, room
 * settings and errors. Matchmaking results are routed by the app's online flow (→ Versus).
 */
import './lobby.css';
import { useEffect, useRef, useState } from 'preact/hooks';
import {
  CHARACTER_IDS,
  characterById,
  DRIVES_PER_PLAYER,
  MatchFormat,
  SpecialsMode,
  specialInfo,
  type RoomSettings,
} from '@hf/shared';
import type { LeaderboardEntry } from '@hf/client-core/net/types';
import { useActions, useServices } from '../actions';
import { Head, SpecialIcon } from '../components';
import { lobbyTab, onlineCharacter, type LobbyTab, type VenueSetting } from '@hf/client-core/ui-model/flow';
import {
  KICKOFF_CALL,
  RANK_TIERS,
  rankProgress,
  rankTier,
  rankTierIndex,
  thousands,
  waitTime,
} from '@hf/client-core/ui-model/format';
import { BLOCK_CONFIRM, REPORT_REASONS, moderationOutcome, runModeration } from '@hf/client-core/ui-model/moderation';
import type { PlayerReportReason } from '@hf/client-core/net/types';
import { settingsForRoom } from '@hf/client-core/ui-model/room-watch';
import { useFocusGrid } from '../nav';
import { playSfx, UI_SFX } from '@hf/client-core/ui-model/sfx';
import { ui } from '@hf/client-core/ui-model/store';
import { cycle, Shell, StepRow } from './Shell';

const TABS: readonly { id: LobbyTab; label: string }[] = [
  { id: 'quick', label: 'QUICK MATCH' },
  { id: 'ranked', label: 'RANKED' },
  { id: 'private', label: 'PRIVATE ROOM' },
  { id: 'leaderboard', label: 'LEADERBOARD' },
];
const ROOM_DRIVES = [2, 3, 4, 6, 8] as const;
/** Classic is offered once the sim has its kick plays (kickoff / punt / onside tosses). Ranked always plays Drives. */
const ROOM_FORMATS: readonly MatchFormat[] =
  KICKOFF_CALL >= 0 ? [MatchFormat.DRIVES, MatchFormat.CLASSIC] : [MatchFormat.DRIVES];
const ROOM_VENUES: readonly VenueSetting[] = ['auto', 'day', 'night'];
const VENUE_NAMES: Record<VenueSetting, string> = {
  auto: 'SURPRISE ME',
  day: 'DAY GAME',
  night: 'NIGHT GAME',
};

/** The room card's rules line: "CLASSIC · SPECIALS ON · NIGHT GAME", "4 DRIVES · SPECIALS OFF". */
/** A second Esc / B within this window leaves the room. */
export const LEAVE_ARM_MS = 2500;

export const roomSettingsLine = (r: RoomSettings): string =>
  [
    r.format === MatchFormat.CLASSIC ? 'CLASSIC 2 × 2:00' : `${r.drivesPerPlayer} DRIVES`,
    `SPECIALS ${r.specials === SpecialsMode.OFF ? 'OFF' : 'ON'}`,
    ...(r.timeOfDay ? [`${r.timeOfDay === 'night' ? 'NIGHT' : 'DAY'} GAME`] : []),
  ].join(' · ');

/** Normalize a typed room code: letters only, upper case, max 4. */
export const normalizeCode = (raw: string): string =>
  raw
    .toUpperCase()
    .replace(/[^A-Z]/g, '')
    .slice(0, 4);

/**
 * Ask for the leaderboard. `OnlineClient.requestLeaderboard` resolves with the entries; older clients only had
 * a raw `send` (entries then arrive as a 'leaderboard' message). Returns null when nothing could be sent.
 */
export const requestLeaderboard = (
  online: unknown,
  limit = 20,
): Promise<LeaderboardEntry[] | null> | null => {
  const o = online as {
    requestLeaderboard?: (n: number) => Promise<LeaderboardEntry[]> | void;
    send?: (m: { type: 'leaderboard_get'; limit: number }) => void;
  };
  try {
    if (typeof o.requestLeaderboard === 'function') {
      const r = o.requestLeaderboard(limit);
      return r && typeof (r as Promise<unknown>).then === 'function'
        ? (r as Promise<LeaderboardEntry[]>)
        : Promise.resolve(null);
    }
    if (typeof o.send === 'function') {
      o.send({ type: 'leaderboard_get', limit });
      return Promise.resolve(null);
    }
  } catch {
    /* fall through */
  }
  return null;
};

const isUp = (s: string): boolean => s !== 'offline' && s !== 'connecting' && s !== 'error';

interface Item {
  id: string;
  disabled?: boolean;
  adjust?(dir: -1 | 1): void;
  run?(): void;
}

export function OnlineLobby() {
  const actions = useActions();
  const { online, audio } = useServices();
  const net = ui.net.value;
  const tab = lobbyTab.value;
  const character = onlineCharacter.value;
  const connected = isUp(net.status);
  const [code, setCode] = useState('');
  const [codeError, setCodeError] = useState<string | null>(null);
  const [drives, setDrives] = useState<number>(DRIVES_PER_PLAYER);
  const [specials, setSpecials] = useState<SpecialsMode>(SpecialsMode.SIGNATURE);
  const [format, setFormat] = useState<MatchFormat>(MatchFormat.DRIVES);
  const [venue, setVenue] = useState<VenueSetting>('auto');
  const [board, setBoard] = useState<LeaderboardEntry[] | null>(null);
  const [connectErr, setConnectErr] = useState<string | null>(null);
  const [queuedAt, setQueuedAt] = useState<number | null>(null);
  const [, tick] = useState(0);
  const [leaveArmed, setLeaveArmedState] = useState(false);
  const leaveArmedRef = useRef(false);
  const leaveTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const setLeaveArmed = (on: boolean): void => {
    leaveArmedRef.current = on;
    setLeaveArmedState(on);
    if (leaveTimer.current) clearTimeout(leaveTimer.current);
    leaveTimer.current = on ? setTimeout(() => setLeaveArmed(false), LEAVE_ARM_MS) : null;
  };
  useEffect(
    () => () => {
      if (leaveTimer.current) clearTimeout(leaveTimer.current);
    },
    [],
  );
  const codeRef = useRef<HTMLInputElement | null>(null);

  const loadBoard = (): void => {
    const p = requestLeaderboard(online, 20);
    p?.then((entries) => {
      if (Array.isArray(entries)) setBoard(entries);
    }).catch(() => setBoard((b) => b ?? []));
  };
  const connect = (): void => {
    setConnectErr(null);
    online
      .connect()
      .then(() => {
        const name = ui.settings.peek().playerName.trim();
        if (name) online.setName(name);
        if (typeof online.requestRating === 'function') online.requestRating().catch(() => undefined);
        if (lobbyTab.peek() === 'leaderboard') loadBoard();
      })
      .catch((e: unknown) => setConnectErr(e instanceof Error ? e.message : 'Could not reach the server'));
  };

  useEffect(() => {
    if (!online.connected) connect();
    const subs = [
      online.on('leaderboard', (m) => setBoard(m.entries)),
      online.on('room_closed', (m) => {
        ui.toast(`Room ${m.code} closed: ${m.reason}`, 'info');
      }),
      online.on('error', (m) => {
        if (m.code === 'room_not_found') setCodeError('No room with that code');
        else if (m.code === 'room_full') setCodeError('That room is full');
        else if (m.code !== 'rate_limited') ui.toast(m.message, 'error');
      }),
    ];
    return () => {
      for (const u of subs) u();
    };
  }, []);

  useEffect(() => {
    if (tab === 'leaderboard' && connected) loadBoard();
  }, [tab, connected]);

  // Smooth queue timer between the server's queue_status updates.
  const queueing = net.status === 'queueing';
  useEffect(() => {
    if (!queueing) {
      setQueuedAt(null);
      return;
    }
    setQueuedAt((q) => q ?? Date.now() - (net.queue?.waitingMs ?? 0));
    const t = setInterval(() => tick((x) => x + 1), 250);
    return () => clearInterval(t);
  }, [queueing]);

  const room = net.room;
  const roomRules = settingsForRoom(room?.code);
  const me = room?.players.find((p) => p.id === net.playerId);
  const meReady = !!me?.ready;

  const safe = (fn: () => void): void => {
    try {
      fn();
    } catch (e) {
      ui.toast(e instanceof Error ? e.message : 'Online play is unavailable', 'error');
      playSfx(audio, UI_SFX.error);
    }
  };
  const findMatch = (mode: 'quick' | 'ranked'): void =>
    safe(() => {
      playSfx(audio, UI_SFX.confirm);
      online.queue(mode, onlineCharacter.peek());
    });
  const cancel = (): void => safe(() => online.leaveQueue());
  const join = (): void => {
    const c = normalizeCode(code);
    if (c.length !== 4) {
      setCodeError('Room codes are 4 letters');
      playSfx(audio, UI_SFX.error);
      return;
    }
    setCodeError(null);
    safe(() => online.joinRoom(c, onlineCharacter.peek()));
  };
  const create = (): void =>
    safe(() =>
      online.createRoom(onlineCharacter.peek(), {
        format,
        specials,
        drivesPerPlayer: drives,
        ...(venue !== 'auto' ? { timeOfDay: venue } : {}),
      }),
    );
  const setChar = (dir: -1 | 1): void => {
    const next = cycle(CHARACTER_IDS, onlineCharacter.peek(), dir);
    onlineCharacter.value = next;
    playSfx(audio, UI_SFX.move);
    if (room && connected) safe(() => online.setReady(meReady, next));
  };
  const setTab = (dir: -1 | 1): void => {
    lobbyTab.value = cycle(
      TABS.map((t) => t.id),
      lobbyTab.peek(),
      dir,
    );
    playSfx(audio, UI_SFX.move);
  };

  const content: Item[] = !connected
    ? [{ id: 'retry', run: connect, disabled: net.status === 'connecting' }]
    : tab === 'quick' || tab === 'ranked'
      ? queueing
        ? [{ id: 'cancel', run: cancel }]
        : [{ id: 'find', run: () => findMatch(tab) }]
      : tab === 'private'
        ? room
          ? [
              { id: 'ready', run: () => safe(() => online.setReady(!meReady, onlineCharacter.peek())) },
              {
                id: 'leave',
                run: () => {
                  setLeaveArmed(false);
                  safe(() => online.leaveRoom());
                },
              },
            ]
          : [
              ...(ROOM_FORMATS.length > 1
                ? [{ id: 'format', adjust: (d: -1 | 1) => setFormat((v) => cycle(ROOM_FORMATS, v, d)) }]
                : []),
              {
                id: 'drives',
                adjust: (d) =>
                  setDrives((v) => cycle(ROOM_DRIVES, v as (typeof ROOM_DRIVES)[number], d, false)),
              },
              {
                id: 'specials',
                adjust: (d) =>
                  setSpecials((v) =>
                    cycle([SpecialsMode.SIGNATURE, SpecialsMode.OFF] as const, v as 0 | 1, d),
                  ),
              },
              { id: 'venue', adjust: (d) => setVenue((v) => cycle(ROOM_VENUES, v, d)) },
              { id: 'create', run: create },
              { id: 'code', run: () => codeRef.current?.focus() },
              { id: 'join', run: join },
            ]
        : [{ id: 'refresh', run: () => loadBoard() }];
  const items: Item[] = [{ id: 'tabs', adjust: setTab }, { id: 'head', adjust: setChar }, ...content];

  /** The ◀ arrow: out of Online altogether (cancels the search, leaves the room). */
  const exit = (): void => {
    playSfx(audio, UI_SFX.back);
    if (queueing) cancel();
    if (room) safe(() => online.leaveRoom());
    actions.goto('main_menu');
  };
  /**
   * Esc / B steps out ONE level (QA r3: one Esc while SEARCHING… dropped the player out of Online, one Esc in a room
   * booted the friend's host): searching → cancel the search and stay on the tab; in a room → the first press arms
   * LEAVE ROOM (focus + "press again"), a second one within LEAVE_ARM_MS leaves it and stays on PRIVATE ROOM.
   */
  const leave = (): void => {
    if (queueing) {
      playSfx(audio, UI_SFX.back);
      cancel();
      return;
    }
    if (room) {
      if (leaveArmedRef.current) {
        playSfx(audio, UI_SFX.back);
        setLeaveArmed(false);
        safe(() => online.leaveRoom());
        return;
      }
      playSfx(audio, UI_SFX.toggle);
      setLeaveArmed(true);
      const li = idx('leave');
      if (li >= 0) grid.setIndex(li);
      return;
    }
    exit();
  };
  const grid = useFocusGrid({
    count: items.length,
    initial: 2,
    isDisabled: (i) => !!items[i]?.disabled,
    onMove: () => playSfx(audio, UI_SFX.move),
    onAdjust: (i, dir) => items[i]?.adjust?.(dir),
    onConfirm: (i) => {
      const it = items[i];
      if (it?.run) it.run();
      else if (it?.adjust) it.adjust(1);
    },
    onBack: leave,
  });
  const idx = (id: string): number => items.findIndex((x) => x.id === id);
  // Entering a room (created or joined with Enter / A) puts focus on I'M READY: the old index would land on the red
  // LEAVE ROOM, and the next Enter would drop the player out. Leaving a room goes back to CREATE ROOM.
  const roomCode = room?.code ?? null;
  const prevRoomCode = useRef(roomCode);
  useEffect(() => {
    const before = prevRoomCode.current;
    prevRoomCode.current = roomCode;
    if (roomCode === before) return;
    const target = idx(roomCode ? 'ready' : 'create');
    if (target >= 0) grid.setIndex(target);
  }, [roomCode]);
  const itemProps = (id: string): Record<string, unknown> => {
    const i = idx(id);
    return i < 0 ? {} : grid.item(i);
  };

  const rating = net.rating;
  const def = characterById(character);
  return (
    <Shell
      screen="online_lobby"
      title={tab === 'ranked' ? 'RANKED' : 'ONLINE'}
      kicker={statusLine(net.status, net.message, net.rttMs)}
      class="hf-lobby"
      onBack={exit}
      stadium="night"
      rays={{ a: '#1f3a8a', b: '#0b1030' }}
      hints={[
        ['left|right', grid.index === 0 ? 'TAB' : grid.index === 1 ? 'HEAD' : 'CHANGE'],
        ['confirm', 'SELECT'],
        [
          'back',
          queueing ? 'CANCEL SEARCH' : room ? (leaveArmed ? 'AGAIN TO LEAVE ROOM' : 'LEAVE ROOM') : 'BACK',
        ],
      ]}
    >
      <div class="hf-lobby__grid">
        <aside class="hf-lobby__card hf-panel">
          <div class="hf-lobby__who">
            <div class="hf-lobby__name t-display">
              {(net.name || ui.settings.value.playerName || 'PLAYER').toUpperCase()}
            </div>
            <div class={`hf-lobby__conn t-broadcast ${net.status}`}>
              <span class="hf-dot on" aria-hidden="true" />
              {connected
                ? net.rttMs !== undefined
                  ? `${Math.round(net.rttMs)} MS`
                  : 'ONLINE'
                : net.status === 'connecting'
                  ? 'CONNECTING'
                  : 'OFFLINE'}
            </div>
          </div>
          {rating ? (
            <div class="hf-lobby__rating">
              <div class="hf-lobby__elo t-broadcast">
                <span>{rankTier(rating.rating)}</span>
                <b>{thousands(rating.rating)}</b>
              </div>
              <div class="hf-lobby__tier">
                <span style={{ width: `${Math.round(rankProgress(rating.rating) * 100)}%` }} />
              </div>
              <div class="hf-lobby__record t-broadcast">
                {rating.wins}W · {rating.losses}L · {rating.games} {rating.games === 1 ? 'GAME' : 'GAMES'}
              </div>
            </div>
          ) : (
            <div class="hf-lobby__record t-broadcast">UNRANKED · PLAY TO GET A RATING</div>
          )}
          <div
            class="hf-lobby__pick hf-row interactive"
            {...itemProps('head')}
            role="group"
            aria-label="Your head"
          >
            <button
              class="hf-steprow__arrow"
              tabIndex={-1}
              aria-label="Previous head"
              onClick={(e) => (e.stopPropagation(), setChar(-1))}
            >
              ◀
            </button>
            <div class="hf-lobby__head" key={character}>
              <Head character={character} expression="determined" class="hf-lobby__headart" />
            </div>
            <button
              class="hf-steprow__arrow"
              tabIndex={-1}
              aria-label="Next head"
              onClick={(e) => (e.stopPropagation(), setChar(1))}
            >
              ▶
            </button>
          </div>
          <div class="hf-lobby__headname t-display">{def.name}</div>
          <div class="hf-lobby__special">
            <span class="hf-lobby__spicon">
              <SpecialIcon special={def.signature} />
            </span>
            <span class="t-broadcast">
              {tab === 'ranked' ? 'SPECIALS OFF IN RANKED' : specialInfo(def.signature).name}
            </span>
          </div>
        </aside>

        <section class="hf-lobby__main">
          <div class="hf-lobby__tabs hf-row interactive" {...itemProps('tabs')} role="tablist">
            {TABS.map((t) => (
              <button
                key={t.id}
                role="tab"
                data-testid={`lobby-tab-${t.id}`}
                aria-selected={tab === t.id}
                // roving focus: the row is the one Tab stop (◀ ▶ switch tabs), so Tab never rings an unselected
                // tab inside the yellow-ringed row (QA r3)
                tabIndex={-1}
                class={`hf-lobby__tab t-broadcast ${tab === t.id ? 'on' : ''}`}
                onClick={(e) => {
                  e.stopPropagation();
                  lobbyTab.value = t.id;
                }}
              >
                {t.label}
              </button>
            ))}
          </div>

          <div class="hf-lobby__body hf-panel" key={tab}>
            {!connected ? (
              <div class="hf-lobby__offline">
                <div
                  class={`hf-lobby__spinner ${net.status === 'connecting' ? '' : 'off'}`}
                  aria-hidden="true"
                />
                <div class="t-display hf-lobby__big">
                  {net.status === 'connecting' ? 'CONNECTING TO HFN…' : 'CAN’T REACH THE SERVER'}
                </div>
                <p>{connectErr ?? net.message ?? 'Check your connection and try again.'}</p>
                <button
                  class="hf-btn interactive"
                  {...itemProps('retry')}
                  disabled={net.status === 'connecting'}
                >
                  RETRY
                </button>
              </div>
            ) : tab === 'quick' || tab === 'ranked' ? (
              <QueuePanel
                mode={tab}
                queueing={queueing && (net.queue?.mode ?? tab) === tab}
                waitedMs={queuedAt !== null ? Date.now() - queuedAt : 0}
                players={net.queue?.playersInQueue ?? 0}
                rating={rating?.rating}
                findProps={itemProps('find')}
                cancelProps={itemProps('cancel')}
              />
            ) : tab === 'private' ? (
              room ? (
                <div class="hf-room">
                  <div class="hf-room__code">
                    <span class="t-broadcast">ROOM CODE</span>
                    <div
                      class="hf-room__letters"
                      aria-label={`Room code ${room.code.split('').join(' ')}`}
                      data-testid="lobby-room-letters"
                    >
                      {room.code.split('').map((ch, i) => (
                        <b class="t-display" key={i}>
                          {ch}
                        </b>
                      ))}
                    </div>
                    <small>Tell your friend this code. They pick PRIVATE ROOM → JOIN.</small>
                  </div>
                  {roomRules ? (
                    <div class="hf-room__settings t-broadcast">{roomSettingsLine(roomRules)}</div>
                  ) : null}
                  <div class="hf-room__roster">
                    {[0, 1].map((i) => {
                      const p = room.players[i];
                      return (
                        <div
                          class={`hf-room__slot ${i === 0 ? 'home' : 'away'} ${p?.ready ? 'ready' : ''} ${p ? '' : 'empty'}`}
                          key={i}
                        >
                          {p ? (
                            <>
                              <Head character={p.character} class="hf-room__head" flip={i === 1} />
                              <div class="hf-room__pname">
                                <b class="t-display">{p.name.toUpperCase()}</b>
                                <small class="t-broadcast">
                                  {p.id === room.hostId ? 'HOST · ' : ''}
                                  {thousands(p.rating)}
                                </small>
                              </div>
                              <span class="hf-room__ready t-display">{p.ready ? 'READY!' : 'NOT READY'}</span>
                            </>
                          ) : (
                            <div class="hf-room__waiting t-broadcast">
                              <span class="hf-lobby__spinner small" aria-hidden="true" /> WAITING FOR A
                              FRIEND…
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                  <div class="hf-room__btns">
                    <button
                      class={`hf-btn ${meReady ? 'dark' : 'lime'} interactive`}
                      data-testid="lobby-ready"
                      {...itemProps('ready')}
                    >
                      {meReady ? 'NOT READY' : "I'M READY!"}
                    </button>
                    <button
                      class={`hf-btn red interactive ${leaveArmed ? 'armed' : ''}`}
                      data-testid="lobby-leave-room"
                      {...itemProps('leave')}
                    >
                      {leaveArmed ? 'PRESS AGAIN TO LEAVE' : 'LEAVE ROOM'}
                    </button>
                  </div>
                </div>
              ) : (
                <div class="hf-private">
                  <div class="hf-private__col">
                    <div class="hf-private__title t-display">HOST A ROOM</div>
                    {ROOM_FORMATS.length > 1 ? (
                      <StepRow
                        label="FORMAT"
                        value={format === MatchFormat.CLASSIC ? 'CLASSIC' : 'DRIVES'}
                        focused={grid.index === idx('format')}
                        onStep={(d) => setFormat((v) => cycle(ROOM_FORMATS, v, d))}
                        item={itemProps('format')}
                      />
                    ) : null}
                    <StepRow
                      label={format === MatchFormat.CLASSIC ? 'HALVES' : 'DRIVES'}
                      value={format === MatchFormat.CLASSIC ? '2 × 2:00' : `${drives} EACH`}
                      focused={grid.index === idx('drives')}
                      onStep={(d) =>
                        setDrives((v) => cycle(ROOM_DRIVES, v as (typeof ROOM_DRIVES)[number], d, false))
                      }
                      item={itemProps('drives')}
                    />
                    <StepRow
                      label="SPECIALS"
                      value={specials === SpecialsMode.OFF ? 'OFF' : 'SIGNATURE'}
                      focused={grid.index === idx('specials')}
                      onStep={(d) =>
                        setSpecials((v) =>
                          cycle([SpecialsMode.SIGNATURE, SpecialsMode.OFF] as const, v as 0 | 1, d),
                        )
                      }
                      item={itemProps('specials')}
                    />
                    <StepRow
                      label="STADIUM"
                      value={VENUE_NAMES[venue]}
                      focused={grid.index === idx('venue')}
                      onStep={(d) => setVenue((v) => cycle(ROOM_VENUES, v, d))}
                      item={itemProps('venue')}
                    />
                    <button
                      class="hf-btn interactive"
                      data-testid="lobby-create-room"
                      {...itemProps('create')}
                    >
                      CREATE ROOM
                    </button>
                  </div>
                  <div class="hf-private__or t-display">OR</div>
                  <div class="hf-private__col">
                    <div class="hf-private__title t-display">JOIN A FRIEND</div>
                    <div class="hf-row hf-private__code interactive" {...itemProps('code')}>
                      <span class="t-display">CODE</span>
                      <input
                        ref={codeRef}
                        class="hf-input hf-code t-display"
                        value={code}
                        maxLength={4}
                        placeholder="ABCD"
                        autoComplete="off"
                        autoCapitalize="characters"
                        spellcheck={false}
                        aria-label="Room code"
                        data-testid="lobby-room-code"
                        aria-invalid={!!codeError}
                        onInput={(e) => {
                          const v = normalizeCode(e.currentTarget.value);
                          e.currentTarget.value = v;
                          setCode(v);
                          setCodeError(null);
                        }}
                        onKeyDown={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            e.currentTarget.blur();
                            join();
                            grid.setIndex(idx('join'));
                          } else if (e.key === 'Escape') {
                            e.preventDefault();
                            e.currentTarget.blur();
                          }
                        }}
                      />
                    </div>
                    {codeError ? (
                      <div class="hf-private__err" role="alert" data-testid="lobby-code-error">
                        {codeError}
                      </div>
                    ) : null}
                    <button
                      class="hf-btn blue interactive"
                      data-testid="lobby-join-room"
                      {...itemProps('join')}
                    >
                      JOIN ROOM
                    </button>
                  </div>
                </div>
              )
            ) : (
              <Leaderboard entries={board} me={net.name} meId={net.playerId} online={online} refreshProps={itemProps('refresh')} />
            )}
          </div>
        </section>
      </div>
    </Shell>
  );
}

const statusLine = (status: string, message: string | undefined, rtt: number | undefined): string => {
  switch (status) {
    case 'connecting':
      return 'CONNECTING…';
    case 'error':
      return (message ?? 'OFFLINE').toUpperCase();
    case 'offline':
      return 'OFFLINE';
    case 'queueing':
      return 'SEARCHING FOR AN OPPONENT';
    case 'in_room':
      return 'PRIVATE ROOM';
    case 'matched':
    case 'in_match':
      return 'MATCH FOUND!';
    default:
      return `HFN ONLINE${rtt !== undefined ? ` · PING ${Math.round(rtt)} MS` : ''} · ROLLBACK NETCODE`;
  }
};

function QueuePanel(p: {
  mode: 'quick' | 'ranked';
  queueing: boolean;
  waitedMs: number;
  players: number;
  rating: number | undefined;
  findProps: Record<string, unknown>;
  cancelProps: Record<string, unknown>;
}) {
  if (p.queueing)
    return (
      <div class="hf-queue searching">
        <div class="hf-queue__radar" aria-hidden="true">
          <span />
          <span />
          <span />
        </div>
        <div class="t-display hf-lobby__big">SEARCHING…</div>
        <div class="hf-queue__stats t-broadcast">
          <span>WAIT {waitTime(p.waitedMs)}</span>
          <span>{p.players} IN QUEUE</span>
        </div>
        <button class="hf-btn red interactive" data-testid="lobby-cancel-queue" {...p.cancelProps}>
          CANCEL
        </button>
      </div>
    );
  return (
    <div class="hf-queue">
      {p.mode === 'ranked' ? (
        <>
          <div class="t-display hf-lobby__big">CLIMB THE LADDER</div>
          <p>Equal drives, same yard line, identical heads, no specials, no wind. Pure reads.</p>
          <div class="hf-ladder" role="list">
            {RANK_TIERS.map((t, i) => (
              <span
                key={t.name}
                role="listitem"
                class={`hf-ladder__step t-broadcast tier-${i} ${p.rating !== undefined && i === rankTierIndex(p.rating) ? 'current' : ''}`}
              >
                {t.name}
              </span>
            ))}
          </div>
        </>
      ) : (
        <>
          <div class="t-display hf-lobby__big">QUICK MATCH</div>
          <p>
            Casual rules with signature specials. We'll find someone at your level — no rating on the line.
          </p>
        </>
      )}
      <button class="hf-btn hf-queue__go interactive" data-testid="lobby-find-match" {...p.findProps}>
        FIND MATCH ▶
      </button>
      <div class="hf-queue__rules" role="list" aria-label="Match rules">
        {QUEUE_RULES[p.mode].map(([k, v, sub]) => (
          <div class="hf-queue__rule" role="listitem" key={k}>
            <small class="t-broadcast">{k}</small>
            <b class="t-display">{v}</b>
            <span>{sub}</span>
          </div>
        ))}
      </div>
    </div>
  );
}

/** What a queue plays (server: casual = default config + random wind; ranked = rankedMatchConfig). */
const QUEUE_RULES: Record<'quick' | 'ranked', readonly [string, string, string][]> = {
  quick: [
    ['FORMAT', '4 DRIVES EACH', 'Most points after 8 drives wins'],
    ['SPECIALS', 'SIGNATURE', 'Fill POWER, fire your head\u2019s move'],
    ['HEADS', 'REAL STATS', 'Every head plays its own way'],
    ['RATING', 'NOT AFFECTED', 'Play loose, try things'],
  ],
  ranked: [
    ['FORMAT', '4 DRIVES EACH', 'Same yard line for both sides'],
    ['SPECIALS', 'OFF', 'No power moves: pure reads'],
    ['HEADS', 'EQUAL STATS', 'Pick a look, not an edge'],
    ['RATING', 'ELO ON THE LINE', 'Win to climb the ladder'],
  ],
};

function Leaderboard({
  entries,
  me,
  meId,
  online,
  refreshProps,
}: {
  entries: LeaderboardEntry[] | null;
  me: string | undefined;
  meId: string | undefined;
  online: { reportPlayer?: Function; blockPlayer?: Function } | null | undefined;
  refreshProps: Record<string, unknown>;
}) {
  const act = async (
    e: LeaderboardEntry,
    op: { kind: 'report'; reason: PlayerReportReason } | { kind: 'block' },
  ): Promise<void> => {
    if (!e.id || e.id === meId) return;
    if (op.kind === 'block' && !window.confirm(BLOCK_CONFIRM(e.name))) return;
    const r = await runModeration(online as never, op.kind === 'report'
      ? { kind: 'report', playerId: e.id, reason: op.reason }
      : { kind: 'block', playerId: e.id });
    const o = moderationOutcome(r, e.name);
    ui.toast(o.text, o.tone);
  };
  return (
    <div class="hf-board">
      <div class="hf-board__head t-broadcast">
        <span>#</span>
        <span>PLAYER</span>
        <span>TIER</span>
        <span>ELO</span>
        <span>GAMES</span>
        <span></span>
      </div>
      <div class="hf-board__rows">
        {entries === null ? (
          <div class="hf-board__empty t-broadcast">LOADING THE STANDINGS…</div>
        ) : entries.length === 0 ? (
          <div class="hf-board__empty t-broadcast">NO RANKED GAMES YET — BE THE FIRST!</div>
        ) : (
          entries.map((e) => (
            <div
              class={`hf-board__row ${e.name === me ? 'me' : ''} ${e.rank <= 3 ? `top${e.rank}` : ''}`}
              key={`${e.rank}-${e.name}`}
            >
              <span class="t-display">{e.rank}</span>
              <span>{e.name}</span>
              <span class="t-broadcast">{rankTier(e.rating)}</span>
              <span class="t-broadcast">{thousands(e.rating)}</span>
              <span class="t-broadcast">{e.games}</span>
              {e.id && e.id !== meId ? (
                <span class="hf-board__mod">
                  <button
                    type="button"
                    class="hf-board__mod-btn"
                    title="Report"
                    onClick={() => {
                      const pick = window.prompt(
                        `Report ${e.name}:\n` + REPORT_REASONS.map((r, i) => `${i + 1}. ${r.label}`).join('\n'),
                        '1',
                      );
                      const i = Math.max(1, Math.min(REPORT_REASONS.length, Number(pick) || 1)) - 1;
                      void act(e, { kind: 'report', reason: REPORT_REASONS[i]!.id });
                    }}
                  >
                    Report
                  </button>
                  <button type="button" class="hf-board__mod-btn" title="Block" onClick={() => void act(e, { kind: 'block' })}>
                    Block
                  </button>
                </span>
              ) : (
                <span />
              )}
            </div>
          ))
        )}
      </div>
      <button class="hf-btn dark interactive" {...refreshProps}>
        REFRESH
      </button>
    </div>
  );
}
